import { AxiosResponse } from "axios";
import { IOptions } from "../../api-sdk";
import BaseService from "../baseService";
import {
  IReadContentReportAnalyticsResponse,
  IReadContentAnalyticsQueryPayload,
  IReadLatestContentAnalyticsResponse,
  IReadRevenueAnalyticsQueryPayload,
  IReadRevenueAnalyticsResponse,
  IReadUsersAnalyticsQueryPayload,
  IReadUsersAnalyticsResponse,
  IViewCount,
  IWatchTimeCount,
  IGetTvodAnalyticsQueryPayload,
  IGetTvodAnalyticsResponse,
  IGetTrendingAnalyticsQuery,
  ITrending,
  IHeartBeatPayload,
} from "./analytics.types";
import qs from "qs";

class AnalyticsService extends BaseService {
  constructor(options: IOptions) {
    super(options);
  }

  /**
   * Increments the view count for a given content or episode.
   *
   * @param payload the content id, episode id, and other metadata
   * @returns response from the server
   */
  async countView(payload: IViewCount) {
    return this.request({
      method: "POST",
      url: "/views",
      data: payload,
    });
  }

  /**
   * Logs the watch time for a given content or episode.
   *
   * @param payload the content id, episode id, watch time, and other metadata
   * @returns response from the server
   */
  async countWatchTime(payload: IWatchTimeCount) {
    return this.request({
      method: "POST",
      url: "/watch-time",
      data: payload,
    });
  }

  /**
   * Reads the latest content analytics.
   *
   * @param query the query containing the filter options
   * @returns response from the server containing the latest content analytics
   */
  async readContentAnalytics(
    query?: IReadContentAnalyticsQueryPayload
  ): Promise<AxiosResponse<IReadLatestContentAnalyticsResponse>> {
    if (query) {
      return this.request({
        method: "GET",
        url: `/api/v1/analytics/content?${qs.stringify(query)}`,
      });
    }
    return this.request({
      method: "GET",
      url: "/api/v1/analytics/content",
    });
  }

  /**
   * Reads the users analytics.
   *
   * @param query the query containing the filter options
   * @returns response from the server containing the users analytics
   */
  async readUsersAnalytics(
    query?: IReadUsersAnalyticsQueryPayload
  ): Promise<AxiosResponse<IReadUsersAnalyticsResponse>> {
    if (query) {
      return this.request({
        method: "GET",
        url: `/api/v1/analytics/users?${qs.stringify(query)}`,
      });
    }
    return this.request({
      method: "GET",
      url: "/api/v1/analytics/users",
    });
  }

  /**
   * Reads the content report analytics.
   *
   * @returns response from the server containing the content report analytics
   */
  async readContentReportAnalytics(): Promise<
    AxiosResponse<IReadContentReportAnalyticsResponse>
  > {
    return this.request({
      method: "GET",
      url: "/api/v1/analytics/content-report",
    });
  }

  /**
   * Reads the revenue analytics.
   *
   * @param query Optional query parameters to filter the revenue analytics.
   * @returns A promise that resolves to the server's response containing the revenue analytics.
   */
  async readRevenueAnalytics(
    query?: IReadRevenueAnalyticsQueryPayload
  ): Promise<AxiosResponse<IReadRevenueAnalyticsResponse>> {
    if (query) {
      return this.request({
        method: "GET",
        url: `/api/v1/analytics/revenue-report?${qs.stringify(query)}`,
      });
    }
    return this.request({
      method: "GET",
      url: "/api/v1/analytics/revenue-report",
    });
  }

  /**
   * Fetches the TVOD analytics for the given query.
   *
   * @param query Optional query parameters to filter the TVOD analytics.
   * @returns A promise that resolves to the server's response containing the TVOD analytics.
   */
  async getTvodAnalytics(
    query?: IGetTvodAnalyticsQueryPayload
  ): Promise<AxiosResponse<IGetTvodAnalyticsResponse[]>> {
    if (query) {
      return this.request({
        method: "GET",
        url: `/api/v1/analytics/tvod/content?${qs.stringify(query)}`,
      });
    }
    return this.request({
      method: "GET",
      url: "/api/v1/analytics/tvod/content",
    });
  }

  /**
   * Fetches the trending analytics for the given query.
   *
   * @param query Optional query parameters to filter the trending analytics.
   * @returns A promise that resolves to the server's response containing the trending analytics.
   */
  async trending(
    query?: IGetTrendingAnalyticsQuery
  ): Promise<AxiosResponse<ITrending[]>> {
    return this.request({
      method: "GET",
      url: `/api/v1/analytics/trending?${qs.stringify(query)}`,
    });
  }


  /**
   * Sends a heartbeat signal to track active user sessions and real-time engagement.
   *
   * Call this method at regular intervals (e.g., every 30 seconds) while a user is
   * actively on your platform. It keeps the session alive and, when watching content,
   * reports playback progress so you can build accurate watch-time and concurrency metrics.
   *
   * @param payload - The heartbeat data to send.
   * @param payload.sessionId - A unique identifier for the current user session (e.g., UUID generated on app load).
   * @param payload.currentPage - The page or screen the user is currently on (e.g., `"/watch/abc123"`, `"/home"`).
   * @param payload.platform - *(optional)* The platform the user is on (e.g., `"web"`, `"android"`, `"ios"`).
   * @param payload.contentId - *(optional)* The ID of the content being watched. Required when the user is on a playback screen.
   * @param payload.episodeId - *(optional)* The ID of the episode being watched, if the content is a series.
   * @param payload.contentTitle - *(optional)* The display title of the content (used for human-readable analytics).
   * @param payload.episodeTitle - *(optional)* The display title of the episode.
   * @param payload.videoTime - *(optional)* The current playback position in seconds.
   * @param payload.duration - *(optional)* The total duration of the video in seconds.
   * @returns A promise resolving to `{ success: true }` when the heartbeat is recorded.
   *
   * @example
   * // Basic heartbeat on a non-playback page
   * await zott.analytics.heartbeat({
   *   sessionId: "sess_abc123",
   *   currentPage: "/home",
   *   platform: "web",
   * });
   *
   * @example
   * // Heartbeat during video playback
   * await zott.analytics.heartbeat({
   *   sessionId: "sess_abc123",
   *   currentPage: "/watch/movie-xyz",
   *   platform: "web",
   *   contentId: "movie-xyz",
   *   contentTitle: "Inception",
   *   videoTime: 1200,
   *   duration: 8880,
   * });
   */
  async heartbeat(payload: IHeartBeatPayload): Promise<AxiosResponse<{ success: true }>> {
    return this.request({
      method: "POST",
      url: "/api/v1/analytics/heartbeat",
      data: payload,
    });
  }
}

export default AnalyticsService;
